import MidiPlayer from "midi-player-js"
import { MeasureTimemapEntry, Note as note} from "../../sharedTypes/main";
import { triggerEvent } from "../../sharedTypes/pureHelpers";
import { apiSwagger } from "../../shared/api";
import { isValidIndex } from "@/sharedTypes/helpers";
import { notifyStatus } from "@/sharedTypes/coreTypes";
import { ManagerEntity } from "@/shared/globalFunctions";
import { gMsg } from "@/sharedTypes/coreTypes";
import { dpAssert } from "@/utils/helpers";

type MeasureTimemap = MeasureTimemapEntry[];

export async function addMidiInfo(midi: ArrayBuffer,
  timemap: MeasureTimemap,
  allNotes: note[],
  notesHashMap: Record<string, note>
): Promise<number> {

  // this is for webassembly, so it can access notes
  (window as any).currentMidiNotesHashMap = notesHashMap;

  if (!allNotes.length) { return -1; }

  // remove MIDI values if somehow we have them
  for (const note of allNotes) {
    if (note.ornamentNotes && note.ornamentNotes.length > 0) {
      for (const o of note.ornamentNotes) {
        o.timeNoteOnMidi = undefined;
        o.timeNoteOffMidi = undefined;
      }
    }
    note.timeNoteOnMidi = undefined;
    note.timeNoteOffMidi = undefined;
  }

  // We are using this player to parse the MIDI file
  const player = new MidiPlayer.Player()
  player.loadArrayBuffer(midi)

  if (timemap && player.tracks) {

    const allEvents = mergeAllEventsFromAllTracks(player.tracks)
    let noteOnEvents = allEvents.filter((n: any) => n.name === "Note on");
    let noteOffEvents = allEvents.filter((n: any) => n.name === "Note off");

    // if we have only one instrument
    /*
     * One instrument === two staffs
     * Two instruments === fourth staffs
    */
    if (!moreThan2Staffs(allNotes.slice(0, 30))) {
      //
      /*
       * We should remove dulplicate events (events with the same Tick and NoteNumber)
         Only if we have one instrument
         (it's not possible to play same note twice in the same time with one instrument)

         NOTE: we should not remove them if we have more than one instrument
         because in that case,
         it's possible to have notes played at the same time with the same NoteNumber
       */
      const { filteredNoteOnEvents, filteredNoteOffEvents } = removeDuplicateEvents(noteOnEvents, noteOffEvents);
      noteOnEvents = filteredNoteOnEvents;
      noteOffEvents = filteredNoteOffEvents;
    }
    const noteLinks: any = {}
    for (const note of allNotes) {
      noteLinks[note.noteId] = {};
      noteLinks[note.noteId].next = note.next;
      noteLinks[note.noteId].prev = note.prev;
      if (note.next) {
        note.next = note.next.noteId as any;
      }
      if (note.prev) {
        note.prev = note.prev.noteId as any;
      }
    }
    let response: any;
    // handle errors
    try {
      response = await apiSwagger.midi.processMidiCreate({
        timemap,
        noteOnEvents,
        noteOffEvents
      })

      console.log("[DEBUG] MIDI processed", response);

      // Check if backend returned an error (5xx errors are in response.error, not response.data)
      const errorData = response?.error || (!response.data && response);
      const successData = response?.data;
      
      if (!response || errorData || !successData || successData.countOfMissedNotes === -1) {
        const countOfMissedNotes = allNotes.length;
        const errorMsg = errorData?.error 
          ? `MIDI processing error: ${errorData.error}`
          : successData?.error
          ? `MIDI processing error: ${successData.error}`
          : "MIDI and MusicXML don't match. All notes missed.";
        ManagerEntity.gMsg = new gMsg(errorMsg, notifyStatus.failed);
        triggerEvent("midiFailed");
        return countOfMissedNotes;
      }
    } catch (error) {
      ManagerEntity.gMsg = new gMsg(`Failed to process MIDI: ${error}`, notifyStatus.failed);
      triggerEvent("midiFailed");
      return allNotes.length;
    }
    const countOfMissedNotes = response.data.countOfMissedNotes;
    const returnedHashNotes: Record<string, note> = response.data.notesHashMap as any;
    if (returnedHashNotes)
      for (const note of allNotes) {
        note.timeNoteOnMidi = returnedHashNotes[note.noteId].timeNoteOnMidi;
        note.timeNoteOffMidi = returnedHashNotes[note.noteId].timeNoteOffMidi;
        note.ornamentNotes = returnedHashNotes[note.noteId].ornamentNotes
        note.velocityNoteOn = returnedHashNotes[note.noteId].velocityNoteOn
        note.velocityNoteOff = returnedHashNotes[note.noteId].velocityNoteOff
      }
    if (countOfMissedNotes === 0) {
      ManagerEntity.gMsg = new gMsg("Correct midi", notifyStatus.success);
      triggerEvent("midiSuccess")
      // here if no midi present
    } else {
      ManagerEntity.gMsg = new gMsg("Missed notes: " + countOfMissedNotes, notifyStatus.failed);
      triggerEvent("midiFailed")
    }
    if (countOfMissedNotes === 0 || countOfMissedNotes) {
      return countOfMissedNotes;
    } else {
      return -1;
    }
  }
  return -1;
}

function mergeAllEventsFromAllTracks(tracks: any) {
  // New array to store merged events
  let mergedEvents: any = [];

  // Iterate over each track
  tracks.forEach((track: any) => {
    // Get events from the current track
    const events = track.events;

    // Append events to the mergedEvents array
    mergedEvents = mergedEvents.concat(events);
  });

  return mergedEvents;
}

function moreThan2Staffs(notes: note[]) {
  const staffs: number[] = [];
  for (const note of notes) {
    dpAssert(note.midi)
    if (note.midi.staffPos === 0 || note.midi.staffPos) {
      if (!staffs.includes(note.midi.staffPos as number)) {
        staffs.push(note.midi.staffPos as number);
      }
    }
  }
  return staffs.length > 2;
}

function removeDuplicateEvents(eventsNoteOn: any, eventsNoteOff: any): any {
  const indexesToDeleteNoteOn: any = {};
  const indexesToDeleteNoteOff: any = {};
  for (let ind = 0; ind < eventsNoteOn.length; ind++) {
    if (isValidIndex(eventsNoteOn, ind + 1)) {
      if (eventsNoteOn[ind].noteNumber === eventsNoteOn[ind + 1].noteNumber
        && eventsNoteOn[ind].tick === eventsNoteOn[ind + 1].tick
      ) {
        indexesToDeleteNoteOn[ind] = true;

        const noteOffIndex = extractNoteOffEvent(eventsNoteOff, eventsNoteOn, ind, eventsNoteOn[ind].noteNumber)?.noteOffIndex;
        if (noteOffIndex) {
          indexesToDeleteNoteOff[noteOffIndex] = true
        }
      }
    }
  }
  const filteredNoteOnEvents = [];
  const filteredNoteOffEvents: any = [];
  for (let ind = 0; ind < eventsNoteOn.length; ind++) {
    // change this to hash map (indexesToDeleteNoteOn) so it will be faster
    if (!indexesToDeleteNoteOn.hasOwnProperty(ind)) {
      filteredNoteOnEvents.push(eventsNoteOn[ind]);
    }
    if (!indexesToDeleteNoteOff.hasOwnProperty(ind)) {
      filteredNoteOffEvents.push(eventsNoteOff[ind])
    }
  }
  return { filteredNoteOnEvents, filteredNoteOffEvents };
}

export function extractNoteOffEvent(
  noteOffEvents: any[],
  noteOnEvents: any[],
  noteOnIndex: number,
  neededPitch: number
): { noteOffEvent: any, noteOffIndex: number } | undefined {
  const startIndex = noteOnIndex;
  // if we find needed note when goes forward
  let neededIndexFront = Infinity;
  // if we find needed note when goes backward
  let neededIndexBack = Infinity;
  for (let ind = startIndex; ind < startIndex + 50; ind++) {
    if (!isValidIndex(noteOffEvents, ind)) break;
    const event = noteOffEvents[ind];
    if (
      event.noteNumber === neededPitch
      &&
      event.tick > noteOnEvents[noteOnIndex].tick
    ) {
      neededIndexFront = ind;
      break;
    }
  }
  for (let ind = startIndex; ind > startIndex - 50; ind--) {
    if (!isValidIndex(noteOffEvents, ind)) break;
    const event = noteOffEvents[ind];
    if (
      event.noteNumber === neededPitch
      &&
      event.tick > noteOnEvents[noteOnIndex].tick
    ) {
      neededIndexBack = ind;
      break;
    }
  }
  let finalIndex;
  if (neededIndexFront != Infinity || neededIndexBack != Infinity) finalIndex = Math.min(neededIndexFront, neededIndexBack)
  if (finalIndex === 0 || finalIndex) {
    return { noteOffEvent: noteOffEvents[finalIndex], noteOffIndex: finalIndex }
  }
  return undefined;
}

